import React from 'react'
import PageHeading from '../components/common/PageHeading'
import { Chip, Divider } from '@heroui/react'
import { Helmet } from 'react-helmet'

function About() {
    return (
        <div className='w-full'>
            <Helmet>
                <title>About Us - Oxford College</title>
                <meta name="description" content="Learn more about Oxford College, our mission, vision, and values." />
                <meta name="keywords" content="About, Oxford College, Mission, Vision, Values" />
                <meta name="author" content="Oxford College - Gampaha" />
                <meta property="og:title" content="About Us - Oxford College - Gampaha" />
                <meta property="og:description" content="Discover more about Oxford College, including our values and commitment to education." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://oxfordcollege.lk/about" />
                <meta property="og:image" content="https://www.facebook.com/photo/?fbid=127196623392555&set=a.127196626725888" />
            </Helmet>

            <PageHeading title={'About'} imgUrl={'./assets/images/schoolchidren.webp'} alt={'About'} />

            <div className='container w-full mx-auto px-10 grid grid-cols-1 md:grid-cols-2 mt-10 gap-10'>
                <div className="col-span-1 grid gap-5">
                    <div className='block'>
                        <h3 className='uppercase text-5xl font-bebas'>Our Vision</h3>
                        <p>To inspire a lifelong passion for learning, empowering students to excel in English language mastery, critical thinking, and global citizenship, making Oxford College Gampaha a beacon of educational excellence.</p>
                    </div>
                    <div className='block'>
                        <h3 className='uppercase text-5xl font-bebas'>Our Mission</h3>
                        <p>To provide a dynamic, supportive learning environment that nurtures English language proficiency through Cambridge and General English courses, enabling students from ages 4 to 18 to achieve their full potential as confident communicators and responsible global citizens.</p>
                    </div>
                </div>
                <div className='col-span-1 mx-auto flex flex-col  gap-6 items-center'>
                    <img className='size-60 object-cover rounded-full' src='./assets/images/events/school-opening/director.png' alt='director' />
                    <div>
                        <span className='uppercase font-bebas text-2xl block'>Message from</span>
                        <span className='uppercase font-bebas text-5xl block text-neutral-400'>Director</span>
                        <Divider className='my-2' />
                        <p>At Oxford College Gampaha, our mission is to provide quality education that empowers every child to achieve their full potential. As the director, I am proud to lead an institution that offers a strong foundation through our preschool, primary, and Cambridge English programs. With a focus on academic excellence, creativity, and character development, we strive to prepare our students for a bright and successful future. Join us in shaping the leaders of tomorrow.</p>
                    </div>
                </div>
            </div>

            <div className='container mx-auto my-20 w-full px-10 flex flex-col xl:items-end'>
                <div className='w-full xl:w-9/12'>
                    <h3 className='font-bebas text-5xl uppercase'>Objectives</h3>
                </div>

                <div className='bg-primary-blue rounded-3xl w-full flex items-center relative justify-end p-4 xl:p-10'>
                    <img src="./assets/images/arrow-point.png" alt="arrow-point" className='absolute size-80 -top-24 left-6 hidden xl:block' />

                    <div className='w-full xl:w-9/12'>
                        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
                            <div className='flex flex-col gap-2'>
                                <Chip classNames={{ base: "bg-secondary-blue text-primary-blue" }}>Build Language Proficiency</Chip>
                                <span className='text-xs text-white'>Provide top-quality English education to excel in Cambridge and General courses.</span>
                            </div>
                            <div className='flex flex-col gap-2'>
                                <Chip classNames={{ base: "bg-secondary-blue text-primary-blue" }}>Encourage Critical Thinking</Chip>
                                <span className='text-xs text-white'>Develop problem-solving and creativity skills.</span>
                            </div>
                            <div className='flex flex-col gap-2'>
                                <Chip classNames={{ base: "bg-secondary-blue text-primary-blue" }}>Promote Global Awareness</Chip>
                                <span className='text-xs text-white'>Foster empathy and global citizenship.</span>
                            </div>

                            <div className='flex flex-col gap-2'>
                                <Chip classNames={{ base: "bg-secondary-blue text-primary-blue" }}>Inspire Lifelong Learning</Chip>
                                <span className='text-xs text-white'>Cultivate a lasting love for learning.</span>
                            </div>
                            <div className='flex flex-col gap-2'>
                                <Chip classNames={{ base: "bg-secondary-blue text-primary-blue" }}>Boost Confidence</Chip>
                                <span className='text-xs text-white'>Empower clear and confident self-expression.</span>
                            </div>
                            <div className='flex flex-col gap-2'>
                                <Chip classNames={{ base: "bg-secondary-blue text-primary-blue" }}>Enhance Cultural Understanding</Chip>
                                <span className='text-xs text-white'>Fostering respect and appreciation for diverse cultures.</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className='container mx-auto w-full px-10 mt-10 '>
                <h3 className='font-bebas text-5xl uppercase'>Our History</h3>

                <div className='grid grid-cols-1 md:grid-cols-2 grid-rows-2 gap-6 mt-6'>
                    <div className='col-span-1 flex flex-col gap-y-2'>
                        <Chip classNames={{ base: "bg-secondary-blue text-primary-blue" }}>15 Years of Teaching Excellence</Chip>
                        <p>Celebrating over a decade and a half of dedicated service, Oxford College Gampaha has empowered students to achieve academic success and personal growth. Our commitment to educational excellence has established us as a trusted leader in English language instruction, shaping the futures of countless students from ages 4 to 18.</p>
                    </div>

                    <div className='row-span-2 w-full h-full'>
                        <img src="./assets/images/history-section.webp" alt="" className='w-full h-full object-cover rounded-xl' />
                    </div>

                    <div className='col-span-1 flex flex-col gap-y-2'>
                        <Chip classNames={{ base: "bg-secondary-blue text-primary-blue" }}>5 Years of Cambridge English Teaching</Chip>
                        <p>With five successful years as a Cambridge English preparation center, Oxford College Gampaha is proud to offer internationally recognized Cambridge English courses. Our partnership with Cambridge enables us to provide top-tier language education, equipping students with skills that meet global standards and fostering confidence in English communication.</p>
                    </div>
                </div>
            </div>

            {/* <div className='container mx-auto grid grid-cols-1 md:grid-cols-2 px-10 mt-10 gap-6'>
                <div className='col-span-1 w-full h-full'>
                    <img src="./assets/images/awards/certificate1.webp" alt="" className='w-full h-[350px] object-cover rounded-xl' />
                </div>
                <div className='col-span-1 flex flex-col justify-center'>
                    <h3 className='font-bebas text-5xl uppercase'>Annual Coordinators Conference - 2024</h3>
                    <p>Participated in the Annual Coordinators Conference for the year 2024, focusing on collaboration and strategic planning.</p>
                </div>
            </div> */}

            <div className='container mx-auto mt-10 px-10 grid grid-cols-1 lg:grid-cols-2 gap-6'>
                <div className='w-full bg-neutral-100 p-6 rounded-xl flex flex-col gap-y-4'>
                    <div className='flex flex-col  gap-4 items-center'>
                        <div className='w-fit'>
                            <h3 className='font-bebas text-5xl uppercase text-center text-black'>The Pinnacle of the Year Award</h3>
                        </div>
                        <div className='w-full h-[300px]'>
                            <img src="./assets/images/awards/certificate1.webp" alt="" className='w-fit mx-auto h-full object-cover rounded-lg' />
                        </div>
                    </div>
                    <p>The Pinnacle of the Year Award, presented at the Cambridge English Coordinators Conference, recognizes outstanding leadership and contributions to the field of Cambridge English education. It honors those who demonstrate excellence in teaching, innovation, and commitment to student success. This prestigious award reflects dedication to advancing education and supporting the Cambridge English community.</p>
                </div>
                <div className='w-full bg-neutral-100 p-6 rounded-xl flex flex-col gap-y-4'>
                    <div className='flex flex-col  gap-4 items-center'>
                        <div className='w-fit'>
                            <h3 className='font-bebas text-5xl uppercase text-center text-black'>The Emerging Star of the Year Awards</h3>
                        </div>
                        <div className='w-full h-[300px]'>
                            <img src="./assets/images/awards/certificate2.webp" alt="" className='w-fit mx-auto h-full object-cover rounded-lg' />
                        </div>
                    </div>
                    <p>The Emerging Star of the Year Award, presented at the Cambridge English Coordinators Conference, recognizes rising leaders who have shown exceptional promise and impact in the field of Cambridge English education. This award celebrates innovative contributions, dedication, and the potential to shape the future of education. It is a proud achievement that highlights commitment to excellence and continuous growth in the educational community.</p>
                </div>

                <div className='w-full bg-neutral-100 p-6 rounded-xl flex flex-col gap-y-4'>
                    <div className='flex flex-col  gap-4 items-center'>
                        <div className='w-fit'>
                            <h3 className='font-bebas text-5xl uppercase text-center text-black'>Cambridge English Qualification <br /> 2021 - 2022</h3>
                        </div>
                        <div className='w-full h-[300px]'>
                            <img src="./assets/images/awards/certificate3.webp" alt="" className='w-fit mx-auto h-full object-cover rounded-lg' />
                        </div>
                    </div>
                    <p>Successfully completed the Cambridge English Qualification program during the academic period 2021–2022.</p>
                </div>
                <div className='w-full bg-neutral-100 p-6 rounded-xl flex flex-col gap-y-4'>
                    <div className='flex flex-col  gap-4 items-center'>
                        <div className='w-fit'>
                            <h3 className='font-bebas text-5xl uppercase text-center text-black'>Cambridge English Qualification <br /> 2023 - 2024</h3>
                        </div>
                        <div className='w-full h-[300px]'>
                            <img src="./assets/images/awards/certificate4.webp" alt="" className='w-fit mx-auto h-full object-cover rounded-lg' />
                        </div>
                    </div>
                    <p>Achieved the Cambridge English Qualification for the academic year 2023–2024, enhancing proficiency in English.</p>
                </div>

                <div className='w-full bg-neutral-100 p-6 rounded-xl flex flex-col gap-y-4'>
                    <div className='flex flex-col  gap-4 items-center'>
                        <div className='w-fit'>
                            <h3 className='font-bebas text-5xl uppercase text-center text-black'>Cambridge English Qualification <br /> 2024 - 2025</h3>
                        </div>
                        <div className='w-full h-[300px]'>
                            <img src="./assets/images/awards/certificate5.webp" alt="" className='w-fit mx-auto h-full object-cover rounded-lg' />
                        </div>
                    </div>
                    <p>Pursuing or achieved the Cambridge English Qualification for the 2024–2025 period, focusing on advanced English language skills.</p>
                </div>
            </div>

            <div className='container mx-auto mt-10 px-10'>
                <div className='bg-neutral-100 rounded-2xl p-6 flex flex-col gap-y-4'>
                    <h3 className='font-bebas text-5xl uppercase text-primary-blue'>Why Choose Us?</h3>
                    <p>At the core of our teaching philosophy is the belief that every student deserves a customized approach to learning. Our teaching methodologies are designed to adapt to the individual needs of each student, ensuring a personal and effective learning experience. Here are just a few reasons why students and parents choose us:</p>
                    <div className='flex flex-col gap-y-2'>
                        <div className='flex items-center gap-x-6'>
                            <Chip classNames={{ base: "bg-primary-blue text-white font-bold" }}>Student-Centered Approach</Chip>
                            <hr className='border border-neutral-400 hidden md:block border-dashed w-full' />
                        </div>
                        <p>We believe in fostering a supportive environment where students feel encouraged and challenged to improve.</p>
                    </div>

                    <div className='flex flex-col gap-y-2'>
                        <div className='flex items-center gap-x-6'>
                            <Chip classNames={{ base: "bg-primary-blue text-white font-bold" }}>Experienced Educators</Chip>
                            <hr className='border border-neutral-400 hidden md:block border-dashed w-full' />
                        </div>
                        <p>Our teachers are not only certified Cambridge educators but also bring years of hands-on experience, with a passion for making English learning enjoyable and impactful.</p>
                    </div>

                    <div className='flex flex-col gap-y-2'>
                        <div className='flex items-center gap-x-6'>
                            <Chip classNames={{ base: "bg-primary-blue text-white font-bold" }}>Proven Success</Chip>
                            <hr className='border border-neutral-400 hidden md:block border-dashed w-full' />
                        </div>
                        <p>Our 15-year history is filled with stories of students achieving outstanding results, making us a trusted choice for Cambridge English education.</p>
                    </div>
                </div>
            </div>

            <div className='mx-auto container my-10 px-10'>
                <img src="./assets/images/schoolchildren2.png" alt="" className='w-full h-full object-cover rounded-lg max-h-[400px]' />
            </div>
        </div>
    )
}

export default About